import { useState, useCallback, useEffect } from 'react';
import {
  Button, Typography, Tag, Space, Table, Descriptions, message,
  Modal, Divider, Flex, Input,
} from 'antd';
import {
  PlusOutlined, FileTextOutlined, UnorderedListOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import type { ColumnDef } from '@shared/types';
import { DISPLAY_DATE_FORMAT, BASE_CURRENCY_CODE } from '@shared/constants';
import { DataTable, DetailDrawer } from '../../components/shared';
import type { FetchParams } from '../../components/shared';
import {
  fetchPOs, getPO, fetchUnorderedLines, createPO, updatePOStatus,
  type POHeader, type PODetail, type MRLine,
} from '../../api/purchasing';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// ── Status config ──

const PO_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  open: { label: 'Open', color: 'blue' },
  ordered: { label: 'Ordered', color: 'cyan' },
  partially_received: { label: 'Partially Received', color: 'warning' },
  received: { label: 'Received', color: 'success' },
  closed: { label: 'Closed', color: 'default' },
  pending_cancellation: { label: 'Pending Cancel', color: 'volcano' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

// ── Column Definitions ──

const columns: ColumnDef<POHeader>[] = [
  { key: 'poCode', label: 'PO #', dataType: 'string', width: 130 },
  {
    key: 'status', label: 'Status', dataType: 'enum', width: 150,
    options: Object.entries(PO_STATUS_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label, color: cfg.color })),
    render: (v) => {
      const cfg = PO_STATUS_CONFIG[v as string] || { label: v, color: 'default' };
      return <Tag color={cfg.color}>{cfg.label}</Tag>;
    },
  },
  { key: 'vendorId', label: 'Vendor ID', dataType: 'number', width: 100 },
  { key: 'lineCount', label: 'Lines', dataType: 'number', width: 80 },
  { key: 'poTotal', label: 'PO Total', dataType: 'currency', width: 140 },
  { key: 'openValue', label: 'Open Value', dataType: 'currency', width: 140 },
  { key: 'receivedValue', label: 'Received', dataType: 'currency', width: 130 },
  { key: 'poDate', label: 'PO Date', dataType: 'date', width: 130 },
  { key: 'createdAt', label: 'Created', dataType: 'date', width: 130 },
];

// ── PO Line Table Columns ──

const poLineColumns = [
  { title: '#', dataIndex: 'lineNo', key: 'lineNo', width: 50 },
  { title: 'Item Description', dataIndex: 'itemDescription', key: 'itemDescription', width: 200 },
  { title: 'Part #', dataIndex: 'partNumber', key: 'partNumber', width: 100 },
  { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 80, align: 'right' as const },
  {
    title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', width: 120, align: 'right' as const,
    render: (v: number) => `${BASE_CURRENCY_CODE} ${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
  },
  {
    title: 'Line Total', dataIndex: 'lineTotal', key: 'lineTotal', width: 130, align: 'right' as const,
    render: (v: number) => `${BASE_CURRENCY_CODE} ${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
  },
  { title: 'Received', dataIndex: 'totalReceived', key: 'totalReceived', width: 90, align: 'right' as const },
  { title: 'Balance', dataIndex: 'balanceQty', key: 'balanceQty', width: 90, align: 'right' as const },
];

// ── Create PO Modal (select from unordered MR lines) ──

function CreatePOModal({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [unorderedLines, setUnorderedLines] = useState<MRLine[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchUnorderedLines()
      .then(setUnorderedLines)
      .catch(() => message.error('Failed to load unordered lines'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleCreate = async () => {
    if (selectedKeys.length === 0) {
      message.error('Select at least one MR line');
      return;
    }
    setSaving(true);
    try {
      await createPO(selectedKeys, notes || undefined);
      message.success('Purchase Order(s) created');
      setSelectedKeys([]);
      setNotes('');
      onClose();
      onCreated();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: { error: string } } }).response?.data?.error
        : 'Failed to create PO';
      message.error(msg || 'Failed to create PO');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedKeys([]);
    setNotes('');
    onClose();
  };

  const selectedTotal = unorderedLines
    .filter((l) => selectedKeys.includes(l.id))
    .reduce((sum, l) => sum + l.lineTotal, 0);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Create Purchase Order from MR Lines"
      width={900}
      footer={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            loading={saving}
            onClick={handleCreate}
            disabled={selectedKeys.length === 0}
            icon={<ShoppingCartOutlined />}
          >
            Create PO ({selectedKeys.length} lines)
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Select approved MR lines to group into Purchase Orders (auto-grouped by vendor).
      </Text>

      <Table
        dataSource={unorderedLines}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
        scroll={{ y: 400 }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys) => setSelectedKeys(keys as number[]),
        }}
        columns={[
          { title: 'Item Description', dataIndex: 'itemDescription', key: 'itemDescription', width: 200 },
          { title: 'Part #', dataIndex: 'partNumber', key: 'partNumber', width: 100 },
          { title: 'Vendor ID', dataIndex: 'vendorId', key: 'vendorId', width: 90 },
          { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 80, align: 'right' as const },
          {
            title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', width: 120, align: 'right' as const,
            render: (v: number) => `${BASE_CURRENCY_CODE} ${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
          },
          {
            title: 'Line Total', dataIndex: 'lineTotal', key: 'lineTotal', width: 120, align: 'right' as const,
            render: (v: number) => `${BASE_CURRENCY_CODE} ${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
          },
        ]}
      />

      {selectedKeys.length > 0 && (
        <Flex justify="flex-end" style={{ marginTop: 8 }}>
          <Text strong>
            Selected Total: {BASE_CURRENCY_CODE} {selectedTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </Text>
        </Flex>
      )}

      <Divider />
      <Input.TextArea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        placeholder="Optional PO notes..."
      />
    </Modal>
  );
}

// ── Main Page ──

export function POListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<PODetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleFetch = useCallback(async (params: FetchParams) => {
    return fetchPOs(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const openDetail = async (id: number) => {
    setDrawerOpen(true);
    setDetailLoading(true);
    try {
      const data = await getPO(id);
      setDetail(data);
    } catch {
      message.error('Failed to load PO details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!detail) return;
    try {
      await updatePOStatus(detail.id, status);
      const updated = await getPO(detail.id);
      setDetail(updated);
      message.success(`PO status updated to ${PO_STATUS_CONFIG[status]?.label || status}`);
      refresh();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: { error: string } } }).response?.data?.error
        : 'Failed to update status';
      message.error(msg || 'Failed to update status');
    }
  };

  const drawerActions = [];
  if (detail?.status === 'open') {
    drawerActions.push({
      key: 'mark-ordered',
      label: 'Mark as Ordered',
      icon: <ShoppingCartOutlined />,
      onClick: () => handleStatusChange('ordered'),
    });
  }
  if (detail?.status === 'received') {
    drawerActions.push({
      key: 'close',
      label: 'Close PO',
      onClick: () => handleStatusChange('closed'),
    });
  }
  if (detail && ['open', 'ordered', 'partially_received'].includes(detail.status)) {
    drawerActions.push({
      key: 'cancel',
      label: 'Request Cancellation',
      danger: true,
      onClick: () => handleStatusChange('pending_cancellation'),
    });
  }

  const statusCfg = detail ? PO_STATUS_CONFIG[detail.status] || { label: detail.status, color: 'default' } : undefined;

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Purchase Orders</Title>
      </Flex>

      <DataTable<POHeader>
        columns={columns}
        fetchData={handleFetch}
        rowKey="id"
        toolbar={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New PO
          </Button>
        }
        onRow={(record) => ({
          onClick: () => openDetail(record.id),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 1200 }}
      />

      <CreatePOModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />

      <DetailDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDetail(null); }}
        title={detail?.poCode || ''}
        subtitle={detail ? `Vendor ${detail.vendorId}` : undefined}
        status={statusCfg}
        loading={detailLoading}
        actions={drawerActions}
        width={850}
        tabs={detail ? [
          {
            key: 'details',
            label: 'Details',
            icon: <FileTextOutlined />,
            children: (
              <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="PO Code">{detail.poCode}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={statusCfg?.color}>{statusCfg?.label}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Vendor ID">{detail.vendorId}</Descriptions.Item>
                <Descriptions.Item label="Department ID">{detail.departmentId}</Descriptions.Item>
                <Descriptions.Item label="PO Date">
                  {detail.poDate ? dayjs(detail.poDate).format(DISPLAY_DATE_FORMAT) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Lines">{detail.lineCount}</Descriptions.Item>
                <Descriptions.Item label="PO Total">
                  {BASE_CURRENCY_CODE} {Number(detail.poTotal).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </Descriptions.Item>
                <Descriptions.Item label="Open Value">
                  {BASE_CURRENCY_CODE} {Number(detail.openValue).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </Descriptions.Item>
                <Descriptions.Item label="Received Value">
                  {BASE_CURRENCY_CODE} {Number(detail.receivedValue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </Descriptions.Item>
                <Descriptions.Item label="GRNs">{detail.grns?.length ?? 0}</Descriptions.Item>
                {detail.notes && (
                  <Descriptions.Item label="Notes" span={2}>{detail.notes}</Descriptions.Item>
                )}
              </Descriptions>
            ),
          },
          {
            key: 'lines',
            label: `Lines (${detail.lines.length})`,
            icon: <UnorderedListOutlined />,
            children: (
              <Table
                dataSource={detail.lines}
                columns={poLineColumns}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 900 }}
              />
            ),
          },
        ] : undefined}
      />
    </div>
  );
}
